import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/ui/design-system/components/ui/collapsible';
import {
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
    useSidebar,
} from '@/ui/design-system/components/ui/sidebar';
import { generatePath, useRouteMatch } from 'react-router-dom';
import { FiChevronDown } from 'react-icons/fi';
import { IoMdBusiness } from 'react-icons/io';
import Link from '../Link';
import useCountryParam from 'utils/hooks/useCountryParam';
import useRootCategories from 'utils/hooks/useRootCategories';

const brandsByCategoryPath =
    '/explore/:dataSource(tiktok|google|instagram)?/brands/:categoryId?';
const hashtagsPath =
    '/explore/:dataSource(tiktok|instagram)/hashtags/brands/:categoryId?';
const postsPath = '/explore/:dataSource(tiktok)/posts/brands/:categoryId?';

function SidebarBrandsMenu({ isOpen: controlledOpen, onToggle }) {
    const industriesQuery = useRootCategories();
    const { mutateToWithCountryParam } = useCountryParam();
    const [matchesBrandsByCategory, matchesHashtags, matchesPosts] = [
        brandsByCategoryPath,
        hashtagsPath,
        postsPath,
        // eslint-disable-next-line react-hooks/rules-of-hooks
    ].map((path) => useRouteMatch({ path }));

    const match = matchesBrandsByCategory || matchesHashtags || matchesPosts;
    const defaultOpen = !!match;
    const categoryId = match?.params?.categoryId;
    const dataSource = match?.params?.dataSource;
    const isIndustryCategoryId = industriesQuery.data?.find(
        (category) => category.id === categoryId,
    );
    const maybeIndustryCategoryId = isIndustryCategoryId
        ? categoryId
        : undefined;

    const brandsByCategoryTo = mutateToWithCountryParam(
        generatePath(brandsByCategoryPath, {
            dataSource,
            categoryId:
                dataSource === 'google' ? categoryId : maybeIndustryCategoryId,
        }),
    );
    const hashtagsTo = mutateToWithCountryParam(
        generatePath(hashtagsPath, {
            dataSource:
                ['tiktok', 'instagram'].find((ds) => ds === dataSource) ||
                'tiktok',
            categoryId: maybeIndustryCategoryId,
        }),
    );
    const postsTo = mutateToWithCountryParam(
        generatePath(postsPath, {
            dataSource: 'tiktok',
            categoryId: maybeIndustryCategoryId,
        }),
    );

    const { state, setOpen } = useSidebar();
    const open = controlledOpen ?? defaultOpen;

    const handleTriggerClick = (e) => {
        if (state === 'collapsed') {
            e.preventDefault();
            onToggle?.(true);
            setOpen(true);
        }
    };

    return (
        <Collapsible
            open={open}
            onOpenChange={onToggle}
            className="group/collapsible"
        >
            <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                        tooltip="Brands"
                        onClick={handleTriggerClick}
                    >
                        <IoMdBusiness />
                        <span>Brands</span>
                        <FiChevronDown className="ml-auto !text-sidebar-foreground transition-transform group-data-[state=open]/collapsible:rotate-180" />
                    </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <SidebarMenuSub>
                        <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                                asChild
                                isActive={!!matchesBrandsByCategory}
                            >
                                <Link
                                    to={brandsByCategoryTo}
                                    mixpanelContext="Brands By Category Explorer"
                                >
                                    <span>By Category</span>
                                </Link>
                            </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                                asChild
                                isActive={!!matchesHashtags}
                            >
                                <Link
                                    to={hashtagsTo}
                                    mixpanelContext="Brand Hashtags Explorer"
                                >
                                    <span>Hashtags</span>
                                </Link>
                            </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                                asChild
                                isActive={!!matchesPosts}
                            >
                                <Link
                                    to={postsTo}
                                    mixpanelContext="Brand Posts Explorer"
                                >
                                    <span>Posts</span>
                                </Link>
                            </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                    </SidebarMenuSub>
                </CollapsibleContent>
            </SidebarMenuItem>
        </Collapsible>
    );
}

export default SidebarBrandsMenu;
